import React, { useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import LiveChat from './components/livechat';
import { AuthProvider } from './contexts/AuthContext';
import PrivateRoute from './components/PrivateRoute';
import Home from './pages/Home';
import Signup from './pages/Signup';
import Signin from './pages/Signin';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Admin from './pages/Admin';
import Books from './pages/Books';
import Company from './pages/Company';
import Compliance from './pages/Compliance';
import People from './pages/People';
import Tax from './pages/Tax';
import Equity from './pages/Equity';
import FilingsPage from './pages/FilingsPage';
import FormDetails from './components/FormDetails';
import VerifyEmail from './pages/VerifyEmail';
import Subscribe from './pages/Subscribe';
import SubscriptionConfirmation from './pages/SubscriptionConfirmation';
import ServicePurchase from './pages/ServicePurchase';
import ServiceConfirmation from './pages/ServiceConfirmation';
import Profile from './pages/Profile';
import Dashboard from './pages/Dashboard';
import InvoiceList from './pages/InvoiceList';
import PaymentMethod from './pages/PaymentMethod';
import About from './pages/About';
import Contact from './pages/Contact';
import Careers from './pages/Careers';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import DeadlineAlerts from './pages/DeadlineAlerts';
import CurrencyConverter from './pages/CurrencyConverter';
import Perks from './pages/Perks';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import ProductsSupported from './pages/ProductsSupported';
import FilingPurchase from './pages/FilingPurchase';
import FilingConfirmation from './pages/FilingConfirmation';
import NewClientPage from './pages/NewClientPage';
import NonProfitLLCFormPage from './pages/NonProfitLLCFormPage';
import CTALanding from './pages/CTALanding';
import CampaignLanding from './pages/CampaignLanding';
import { trackTelemetryEvent } from './utils/anonymousTelemetry';
import { initializeWebMcp } from './utils/webmcp';
import ThankYou from './pages/ThankYou';
import './styles/App.css';

function AppShell() {
  const location = useLocation();
  const isCampaignPath = location.pathname === '/campaign';
  const lastPathRef = useRef('');

  useEffect(() => {
    initializeWebMcp();
  }, []);

  useEffect(() => {
    const currentPath = `${location.pathname}${location.search || ''}`;
    const prevPath = lastPathRef.current;
    const isFromCampaign = prevPath.startsWith('/campaign');

    trackTelemetryEvent('site_page_view', {
      current_page: location.pathname,
      page_path: location.pathname,
      referrer_page: prevPath || document.referrer || '',
      from_campaign_path: isFromCampaign,
    });

    if (isFromCampaign && location.pathname !== '/campaign') {
      trackTelemetryEvent('campaign_internal_continue', {
        page_path: '/campaign',
        current_page: '/campaign',
        next_page_known: location.pathname,
        destination_page: location.pathname,
      });
    }

    lastPathRef.current = currentPath;
  }, [location.pathname, location.search]);

  return (
    <div className="App">
      {!isCampaignPath && <Header />}
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/signin" element={<Signin />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/books" element={<Books />} />
          <Route path="/company" element={<Company />} />
          <Route path="/compliance" element={<Compliance />} />
          <Route path="/people" element={<People />} />
          <Route path="/tax" element={<Tax />} />
          <Route path="/equity" element={<Equity />} />
          <Route path="/filings" element={<FilingsPage />} />
          <Route path="/forms/:formName" element={<FormDetails />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/perks" element={<Perks />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/product-sheets" element={<ProductsSupported />} />
          <Route path="/free-tools/deadline-alerts" element={<DeadlineAlerts />} />
          <Route path="/free-tools/currency-converter" element={<CurrencyConverter />} />
          <Route path="/new-client" element={<NewClientPage />} />
          <Route path="/new-nonprofit-llc" element={<NonProfitLLCFormPage />} />
          <Route path="/cta-landing" element={<CTALanding />} />
          <Route path="/campaign" element={<CampaignLanding />} />
          <Route path="/pricing" element={<Navigate to={{ pathname: '/campaign', search: location.search }} replace />} />
          <Route path="/thank-you" element={<ThankYou />} />

          {/* Protected routes */}
          <Route
            path="/admin"
            element={
              <PrivateRoute>
                <Admin />
              </PrivateRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <PrivateRoute>
                <Profile />
              </PrivateRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/subscribe"
            element={
              <PrivateRoute>
                <Subscribe />
              </PrivateRoute>
            }
          />
          <Route
            path="/subscription/confirmation"
            element={
              <PrivateRoute>
                <SubscriptionConfirmation />
              </PrivateRoute>
            }
          />
          <Route
            path="/subscription/:id/invoices"
            element={
              <PrivateRoute>
                <InvoiceList />
              </PrivateRoute>
            }
          />
          <Route
            path="/subscription/:id/payment"
            element={
              <PrivateRoute>
                <PaymentMethod />
              </PrivateRoute>
            }
          />
          <Route
            path="/service-purchase"
            element={
              <PrivateRoute>
                <ServicePurchase />
              </PrivateRoute>
            }
          />
          <Route
            path="/service-confirmation"
            element={
              <PrivateRoute>
                <ServiceConfirmation />
              </PrivateRoute>
            }
          />
          <Route
            path="/filing-purchase"
            element={
              <PrivateRoute>
                <FilingPurchase />
              </PrivateRoute>
            }
          />
          <Route
            path="/filing-confirmation"
            element={
              <PrivateRoute>
                <FilingConfirmation />
              </PrivateRoute>
            }
          />
        </Routes>
      </main>
      {!isCampaignPath && <Footer />}
      {!isCampaignPath && <LiveChat />}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <ScrollToTop />
        <AppShell />
      </Router>
    </AuthProvider>
  );
}

export default App;
